import React, { ElementType } from 'react'
import classNames from 'classnames'

import { Button } from '../../../global/button/Button'
import IconButton from '../../../global/iconButton/IconButton'
import { Color } from '../../../icons'
import { CircleInfoLineIcon } from '../../../icons/CircleInfoLineIcon'
import { CloseIcon } from '../../../icons/CloseIcon'
import {
  Text,
  TextSmall,
  TextSmallStrong,
  TextStrong,
  TextTiny,
  TextTinyStrong,
} from '../../Typography/Typography'
import { PageNoticeType, usePageNotice } from '../PageNotice/PageNoticeContext'

import styles from './Note.module.scss'

export interface NoteProps {
  id: string
  title?: string
  message: string
  /* Pages where the notice should be displayed */
  paths?: string[]
  type: PageNoticeType
  icon?: ElementType
  size?: 'tiny' | 'small' | 'normal'
  action?: {
    label: string
    onClick: () => void
  }
  /*
   * 1. Temporary => hides for a set duration of time, based on dismissDuration (if called)
   * 2. Session => hides until the browser tab/window is closed
   * 3. Permanent => hides until localstorage is cleared
   *  */
  dismissType?: 'temporary' | 'session' | 'permanent'
  /* Duration in days for temporary notices */
  dismissDuration?: number
  canClose?: boolean
}

export const Note: React.FC<NoteProps> = (note) => {
  const { dismissNotice } = usePageNotice()

  const handleClose = () => {
    dismissNotice(note.id, note.dismissType || 'temporary', note.dismissDuration || 14)
  }

  const handleAction = () => {
    if (note.action?.onClick) {
      note.action.onClick()
    }
  }

  const iconColor = (): Color => {
    switch (note.type) {
      case 'alert':
        return 'foregroundNegative'
      case 'success':
        return 'foregroundPositive'
      case 'info':
        return 'foregroundAccent'
      case 'attention':
        return 'foregroundWarning'
      default:
        return 'foregroundPrimary'
    }
  }

  const IconComponent = note.icon ?? CircleInfoLineIcon
  const iconSize = {
    tiny: 20,
    small: 22,
    normal: 24,
  }[note.size ?? 'normal']

  const TextComponent = {
    tiny: { Title: TextTinyStrong, Content: TextTiny },
    small: { Title: TextSmallStrong, Content: TextSmall },
    normal: { Title: TextStrong, Content: Text },
  }[note.size ?? 'normal']

  return (
    <div className={classNames(styles.note, styles[note.type])}>
      <IconComponent size={iconSize} color={iconColor()} />
      <div className={styles.innerWrapper}>
        <div className={styles.content}>
          {note.title && (
            <TextComponent.Title className={styles.title}>{note.title}</TextComponent.Title>
          )}
          <TextComponent.Content>{note.message}</TextComponent.Content>
        </div>
        {note.action && (
          <Button appearance='plain' onClick={handleAction}>
            {note.action.label}
          </Button>
        )}
      </div>

      {note.canClose && (
        <IconButton onClick={handleClose} className={classNames(styles.closeButton)}>
          <CloseIcon size={14} />
        </IconButton>
      )}
    </div>
  )
}
